import React, { useCallback, useEffect, useRef } from 'react';
import { playPracticeSound } from '../services/soundEffects';
import './TrainingAchievementOverlay.css';

function TrainingAchievementOverlay({
  isVisible,
  message,
  spriteSrc,
  frameCount = 4,
  frameWidth = 80,
  frameHeight = 80,
  onDone,
  durationMs = 1000,
}) {
  const completedRef = useRef(false);

  const complete = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    onDone?.();
  }, [onDone]);

  useEffect(() => {
    completedRef.current = false;
    if (!isVisible) return undefined;

    playPracticeSound('celebration');

    const handleKeyDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      if (event.repeat) return;
      complete();
    };

    const timer = setTimeout(() => {
      complete();
    }, durationMs);

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [complete, durationMs, isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="training-achievement-overlay" role="status" aria-live="polite">
      <div className="training-achievement-card">
        <div
          className="training-achievement-sprite"
          style={{
            width: `${frameWidth}px`,
            height: `${frameHeight}px`,
            backgroundImage: spriteSrc ? `url(${spriteSrc})` : 'none',
            backgroundSize: `${frameWidth * Math.max(frameCount, 1)}px ${frameHeight}px`,
            animationDuration: `${durationMs}ms`,
            animationTimingFunction: `steps(${Math.max(frameCount, 1)})`,
            '--training-achievement-sprite-play-end-x': `-${frameWidth * Math.max(frameCount, 1)}px`,
          }}
          aria-hidden="true"
          onAnimationEnd={complete}
        />
        <div className="training-achievement-message">{message}</div>
      </div>
    </div>
  );
}

export default TrainingAchievementOverlay;
